/**
 * Post-tutorial destination (guest-first onboarding).
 *
 * When a first-time user finishes (or skips) the tutorial, decide where they go.
 * The core change from the old flow: a cold/organic user flows into **guest mode**
 * (a real anonymous session + first pick) instead of a sign-**up** wall.
 *
 * - org / squad-invite intent → still route to sign-up (they need a real account to
 *   join their squad); reuse the existing sign-up href builder.
 * - explicit returnUrl (deep link into a specific place) → sign-up then return there.
 * - cold / guest / no intent → guest mode.
 *
 * See specs/guest-first-onboarding/spec.md (AC-5, AC-6).
 */

import { ENTRY_INTENT_KIND } from '../types/entryIntent.types';
import type { EntryIntent } from '../types/entryIntent.types';
import { buildPostTutorialSignInHref } from '../../tutorial/helpers/buildPostTutorialSignInHref.helper';

export type PostTutorialDestination =
  | { kind: 'guest-mode' }
  | { kind: 'sign-in'; href: string };

export function postTutorialDestination(
  intent: EntryIntent | null,
  returnUrl?: string,
): PostTutorialDestination {
  const requiresRealAccount =
    intent != null
    && (intent.kind === ENTRY_INTENT_KIND.ORG || intent.kind === ENTRY_INTENT_KIND.SQUAD_INVITE);

  // Invite/org users, or anyone with an explicit deep-link returnUrl, keep the sign-up path.
  if (requiresRealAccount || returnUrl) {
    return { kind: 'sign-in', href: buildPostTutorialSignInHref(intent, returnUrl) };
  }

  // Cold / guest / null → straight into guest mode (no sign-up wall).
  return { kind: 'guest-mode' };
}
